<template>
  <div class="github-status">
    <a
      class="github-status__desktop"
      :href="profileUrl"
      target="_blank"
      rel="noopener noreferrer"
      :aria-label="`GitHub status: ${statusEmoji} ${statusMessage}. View on GitHub.`"
    >
      <span class="github-status__emoji" aria-hidden="true">{{ statusEmoji || '…' }}</span>
      <span class="github-status__text">
        <span class="github-status__label">Current status</span>
        <span class="github-status__message">{{ statusMessage }}</span>
      </span>
    </a>

    <button
      class="github-status__toggle"
      type="button"
      :aria-expanded="String(isExpanded)"
      aria-controls="github-status-details"
      :aria-label="isExpanded ? 'Hide GitHub status' : 'Show GitHub status'"
      @click="isExpanded = !isExpanded"
    >
      <span class="github-status__emoji" aria-hidden="true">{{ statusEmoji || '…' }}</span>
    </button>

    <div v-show="isExpanded" id="github-status-details" class="github-status__details">
      <span class="github-status__label">Current status</span>
      <span class="github-status__message">{{ statusMessage }}</span>
      <a :href="profileUrl" target="_blank" rel="noopener noreferrer">View on GitHub</a>
    </div>
  </div>
</template>

<script setup>
import { onBeforeUnmount, onMounted, ref } from 'vue';
import home from '@/data/home.json';

const profileUrl = home.github.url;
const username = home.profile.username;
const statusEmoji = ref(home.profile.status.emoji);
const statusMessage = ref(home.profile.status.message);
const isExpanded = ref(false);
const refreshIntervalMs = 5 * 60 * 1000;
let refreshIntervalId;
let isFetchingStatus = false;

function parseProfileStatus(markdown) {
  const lines = markdown.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
  const avatarLine = lines.findIndex((line) => (
    line.includes(`View ${username}'s full-sized avatar`)
  ));

  if (avatarLine === -1) {
    throw new Error('GitHub profile response did not contain the profile avatar');
  }

  const emoji = lines[avatarLine + 1];
  const message = lines[avatarLine + 2];

  if (
    !emoji
    || !message
    || message.startsWith('#')
    || !/[\p{Extended_Pictographic}\p{Regional_Indicator}]/u.test(emoji)
  ) {
    return null;
  }

  return { emoji, message };
}

async function fetchGitHubStatus() {
  if (isFetchingStatus) return;
  isFetchingStatus = true;

  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), 15000);

  try {
    const response = await fetch(`https://r.jina.ai/https://github.com/${username}`, {
      headers: {
        Accept: 'text/plain',
        'X-No-Cache': 'true',
      },
      cache: 'no-store',
      signal: controller.signal,
    });

    if (!response.ok) {
      throw new Error(`GitHub profile status request failed with status ${response.status}`);
    }

    const status = parseProfileStatus(await response.text());
    statusEmoji.value = status?.emoji || '';
    statusMessage.value = status?.message || 'No status set';
  } catch (error) {
    console.warn('Unable to load the current GitHub profile status.', error);
  } finally {
    window.clearTimeout(timeoutId);
    isFetchingStatus = false;
  }
}

function refreshWhenVisible() {
  if (document.visibilityState === 'visible') fetchGitHubStatus();
}

onMounted(() => {
  fetchGitHubStatus();
  refreshIntervalId = window.setInterval(fetchGitHubStatus, refreshIntervalMs);
  document.addEventListener('visibilitychange', refreshWhenVisible);
});

onBeforeUnmount(() => {
  window.clearInterval(refreshIntervalId);
  document.removeEventListener('visibilitychange', refreshWhenVisible);
});
</script>
